import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useParams, Link } from 'react-router-dom';
import { 
    ArrowLeftOutlined, 
    CalendarOutlined, 
    ClockCircleOutlined, 
    UserOutlined, 
    ArrowRightOutlined, 
    EyeOutlined,
    ShareAltOutlined,
    HeartOutlined,
    TagsOutlined,
    ReadOutlined,
    GlobalOutlined
} from '@ant-design/icons';
import { getImg } from '../../../Api/getImg';
import { useDoctorQuery } from '../../../hooks/useDoctorQuery';
import drEasyApi from '../../../Api/Customapi';
import { Image, Tag, Divider, Avatar, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import SocialShare from '../../Buttons/SocialMediaButtons';
import MetaTags from '../../../context/MetaHelmet';

export default function BlogDetails() {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const { slug, id } = useParams();
    const { data: doctor } = useDoctorQuery();
    const [loading, setLoading] = useState(false);
    const [blog, setBlog] = useState(null);
    const [isLiked, setIsLiked] = useState(false);
    const [isBookmarked, setIsBookmarked] = useState(false);
    const [readingTime, setReadingTime] = useState(0);

    const isRTL = i18n.language === 'ar';
    const direction = isRTL ? 'rtl' : 'ltr';
    const textAlignment = isRTL ? 'text-right' : 'text-left';


    // Calculate reading time
    const calculateReadingTime = (content) => {
        const wordsPerMinute = 200;
        const words = content?.replace(/<[^>]+>/g, '').split(/\s+/).length || 0;
        return Math.ceil(words / wordsPerMinute);
    };

    useEffect(() => {
        const fetchBlogDetails = async () => {
            setLoading(true);
            try {
                const response = await drEasyApi.get(`/api/Blogs/getByID`, {
                    params: { Id: id }
                });
                const currentBlog = response.data;
                setBlog(currentBlog);
                setReadingTime(calculateReadingTime(currentBlog.content || currentBlog.description));
            } catch (err) {
                console.error('Failed to load blog details', err);
            } finally {
                setLoading(false);
            }
        };

        fetchBlogDetails();
    }, [id, i18n.language]);

    // Function to detect if text contains Arabic characters
    const isArabic = (text) => {
        const arabicPattern = /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/;
        return arabicPattern.test(text);
    };

    const formatDate = (dateString) => {
        const locale = isRTL ? 'ar-EG' : 'en-US';
        
        if (!dateString || dateString === "0001-01-01T00:00:00") {
            return new Date().toLocaleDateString(locale, {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
            });
        }
        const date = new Date(dateString);
        return date.toLocaleDateString(locale, {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const formatDateShort = (dateString) => {
        const locale = isRTL ? 'ar-EG' : 'en-US';
        
        if (!dateString || dateString === "0001-01-01T00:00:00") {
            return new Date().toLocaleDateString(locale, {
                month: 'short',
                day: 'numeric',
                year: 'numeric'
            });
        }
        const date = new Date(dateString);
        return date.toLocaleDateString(locale, {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });
    };

    const handleGoBack = () => {
        navigate(-1);
    };

    const handleRelatedBlogClick = (relatedBlog) => {
        navigate(`/${slug}/blog/${relatedBlog.id}`, { 
            state: { 
                blog: { 
                    id: relatedBlog.id, 
                    title: relatedBlog.title, 
                    image: relatedBlog.photoID, 
                    description: relatedBlog.description, 
                    date: relatedBlog.createdAt,
                    content: relatedBlog.content
                } 
            } 
        });
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center" dir={direction}>
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                    <p className="text-gray-600">{t('blogDetails.loading')}</p>
                </div>
            </div>
        );
    }

    if (!blog) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center" dir={direction}>
                <div className="text-center">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('blogDetails.notFound')}</h2>
                    <p className="text-gray-600 mb-6">{t('blogDetails.notFoundMessage')}</p>
                    <button
                        onClick={handleGoBack}
                        className="bg-primary hover:bg-secondary text-white font-semibold py-2 px-6 rounded-lg transition-colors duration-200"
                    >
                        {t('blogDetails.goBack')}
                    </button>
                </div>
            </div>
        );
    }

    // Check if blog content is also Arabic (for mixed content scenarios)
    const hasArabicContent = isArabic(blog.title) || isArabic(blog.content || blog.description);
    const contentDirection = hasArabicContent ? 'rtl' : direction;
    const contentAlignment = hasArabicContent ? 'text-right' : textAlignment;
    
    // Prepare share data
    const currentUrl = window.location.href;
    const blogImage = blog.photoID ? getImg(blog.photoID) : 'https://res.cloudinary.com/dk2uh7sho/image/upload/v1752772138/dreasy_2_bsqyem.png';
    const blogDescription = blog?.description || t('blogDetails.medicalArticle');
    const blogTitle = blog?.title || t('blogDetails.medicalArticle');
    const authorName = blog?.doctor?.name ? `${t('blogDetails.dr')} ${blog.doctor.name}` : t('blogDetails.medicalProfessional');


    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50" dir={direction}>
            {/* Meta Tags for Social Sharing */}
            <MetaTags
                title={blogTitle}
                description={blogDescription}
                image={blogImage}
                url={currentUrl}
                author={authorName}
                publishedTime={blog.createdAt}
            />

            <div className="mx-auto px-4 sm:px-6 lg:px-20 py-8">
                <div className="gap-8">
                    <div className="lg:col-span-3">
                        <article className="bg-white rounded-2xl shadow-xl overflow-hidden" dir={contentDirection}>
                            {blog.photoID && (
                                <div className="relative">
                                    <Image
                                        src={getImg(blog.photoID)}
                                        alt={blog.title}
                                        className="w-full h-64 sm:h-80 lg:h-96 object-cover"
                                        style={{ width: '100vw', height: '400px', objectFit: 'cover' }}
                                        onError={(e) => (e.target.src = 'https://via.placeholder.com/800x400?text=Blog+Image')}
                                    />
                                </div>
                            )}

                            <div className="p-6 sm:p-8 lg:p-10">
                                <div className="flex justify-between items-start gap-4 border-b border-gray-200 pb-6 mb-8">
                                    <div className="flex-1">
                                        <div className={`flex items-center gap-4 mb-6`}>
                                            <Avatar 
                                                size={60}
                                                src={blog.doctor?.photoID ? getImg(blog.doctor.photoID) : undefined}
                                                icon={!blog.doctor?.photoID && <UserOutlined />}
                                            />
                                            <div className={textAlignment}>
                                                <h3 className="font-semibold text-lg text-gray-900">
                                                    {t('blogDetails.center')} . {blog?.doctor ? blog?.doctor : blog?.center?.name || t('blogDetails.unknownAuthor')}
                                                </h3>
                                                <p className="text-gray-600">
                                                    {blog.doctor?.spicilaization || t('blogDetails.medicalProfessional')}
                                                </p>
                                                <p className="text-sm text-gray-500 mt-1">
                                                    {t('blogDetails.publishedOn')} {formatDate(blog.createdAt)}
                                                </p>
                                            </div>
                                        </div>

                                        {/* Article Stats */}
                                        <div className={`flex flex-wrap items-center gap-6 text-sm text-gray-600`}>
                                            <div className={`flex items-center gap-2`}>
                                                <CalendarOutlined className="text-primary" />
                                                <span>{formatDateShort(blog.createdAt)}</span>
                                            </div>
                                            <div className={`flex items-center gap-2`}>
                                                <EyeOutlined className="text-primary" />
                                                <span>{t('blogDetails.medicalArticle')}</span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Social Share Component */}
                                    <SocialShare
                                        url={currentUrl}
                                        title={blogTitle}
                                        description={blogDescription}
                                        image={blogImage}
                                    />
                                </div>

                                {/* Blog Title */}
                                <h1 className={`font-bold text-2xl text-primary mb-6 ${contentAlignment}`}>
                                    {blog.title}
                                </h1>

                                {/* Article Summary */}
                                {blog.description && (
                                    <div className={`mb-8 p-6 bg-cardColor/80 rounded-xl ${isRTL ? 'border-r-4' : 'border-l-4'} border-primary ${contentAlignment}`}>
                                        <h3 className="font-semibold text-gray-900 mb-3 text-lg">{t('blogDetails.articleSummary')}</h3>
                                        <p className="text-gray-700 text-lg leading-relaxed">
                                            {blog.description}
                                        </p>
                                    </div>
                                )}

                                {/* Main Content */}
                                <div className={`prose prose-lg max-w-none ${contentAlignment}`} dir={contentDirection}>
                                    {blog.content ? (
                                        <div 
                                            className="text-gray-700 leading-relaxed"
                                            dangerouslySetInnerHTML={{ __html: blog.content }}
                                        />
                                    ) : (
                                        <div className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                                            {blog.description || t('blogDetails.noContent')}
                                        </div>
                                    )}
                                </div>

                                <Divider />

                                {/* Article Footer */}
                                <div className={`mt-8 pt-6 ${textAlignment}`}>
                                    {/* Share Section at Bottom */}
                                    <div className="mb-6 p-4 bg-cardColor/20 rounded-xl">
                                        <h4 className="font-semibold text-gray-900 mb-4">
                                            {isRTL ? 'شارك هذا المقال' : 'Share this article'}
                                        </h4>
                                        <SocialShare
                                            url={currentUrl}
                                            title={blogTitle}
                                            description={blogDescription}
                                            image={blogImage}
                                        />
                                    </div>

                                    <div className="mt-6 text-sm text-gray-500">
                                        <p>{t('blogDetails.lastUpdated')}: {formatDate(blog.createdAt || new Date())}</p>
                                    </div>
                                </div>
                            </div>
                        </article>
                    </div>
                </div>
            </div>

            <style jsx>{`
                .prose h1, .prose h2, .prose h3, .prose h4, .prose h5, .prose h6 {
                    color: #1f2937;
                    font-weight: 600;
                    line-height: 1.25;
                    margin-top: 2rem;
                    margin-bottom: 1rem;
                }

                .prose p {
                    margin-bottom: 1.5rem;
                    line-height: 1.75;
                }

                .prose ul, .prose ol {
                    margin-bottom: 1.5rem;
                }

                [dir="rtl"] .prose ul,
                [dir="rtl"] .prose ol {
                    padding-right: 2rem;
                    padding-left: 0;
                }

                [dir="ltr"] .prose ul,
                [dir="ltr"] .prose ol {
                    padding-left: 2rem;
                    padding-right: 0;
                }

                .prose li {
                    margin-bottom: 0.5rem;
                }

                .prose blockquote {
                    background: #f8fafc;
                    padding: 1rem 1.5rem;
                    margin: 2rem 0;
                    border-radius: 0.5rem;
                    font-style: italic;
                    color: #475569;
                }

                [dir="rtl"] .prose blockquote {
                    border-right: 4px solid #3b82f6;
                    border-left: none;
                }

                [dir="ltr"] .prose blockquote {
                    border-left: 4px solid #3b82f6;
                    border-right: none;
                }

                .prose img {
                    border-radius: 0.75rem;
                    margin: 2rem 0;
                    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
                }

                .prose a {
                    color: #3b82f6;
                    text-decoration: underline;
                    transition: color 0.2s;
                }

                .prose a:hover {
                    color: #1d4ed8;
                }

                .line-clamp-2 {
                    display: -webkit-box;
                    -webkit-line-clamp: 2;
                    -webkit-box-orient: vertical;
                    overflow: hidden;
                }

                @keyframes pulse {
                    0%, 100% { opacity: 1; }
                    50% { opacity: .5; }
                }

                .animate-pulse {
                    animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
                }

                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }

                .animate-spin {
                    animation: spin 1s linear infinite;
                }
            `}</style>
        </div>
    );
}